// Utilities to normalize HTTP errors (especially Axios errors) across the app

import * as HttpStatus from 'http-status-codes';
import { MSG_PAYMENT_REQUIRED } from '../constants';

const METADATA_KEYS = new Set(['code', 'status', 'type', 'traceId', 'trace_id']);

/**
 * Known API error codes from backend ApiError helpers.
 * Used as fallback when errorMessage is missing from the response body.
 */
export const KNOWN_API_ERROR_MESSAGES = {
  unauthorized_access: 'User is not registered to access this feature.',
  access_denied: 'This function is available to broker users only.',
  brokerage_not_found: 'No active brokerage is associated with this user account.',
  server_error: 'Something went wrong.',
};

/** 401 responses with these codes are feature-access denials, not expired sessions. */
export const BUSINESS_UNAUTHORIZED_CODES = new Set([
  'unauthorized_access',
  'access_denied',
]);

const BUSINESS_UNAUTHORIZED_HANDLED = '__businessUnauthorizedHandled';

export function markBusinessUnauthorizedHandled(error) {
  if (error && typeof error === 'object') {
    Object.defineProperty(error, BUSINESS_UNAUTHORIZED_HANDLED, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  }
}

export function wasBusinessUnauthorizedHandled(error) {
  return Boolean(error && error[BUSINESS_UNAUTHORIZED_HANDLED]);
}

function messageFromErrorItem(item) {
  if (!item) return undefined;
  if (typeof item === 'string' && item.trim()) return item.trim();
  if (typeof item.errorMessage === 'string' && item.errorMessage.trim()) return item.errorMessage.trim();
  if (typeof item.message === 'string' && item.message.trim()) return item.message.trim();
  if (typeof item.description === 'string' && item.description.trim()) return item.description.trim();
  if (typeof item.Description === 'string' && item.Description.trim()) return item.Description.trim();
  return undefined;
}

function tryParseJsonObject(value) {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return undefined;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch (err) {
    return undefined;
  }
}

function extractMessageFromData(data) {
  if (!data) return undefined;
  if (typeof data === 'string') {
    const parsed = tryParseJsonObject(data);
    if (parsed) return extractMessageFromData(parsed);
    return data.trim() || undefined;
  }

  // BE standard: { code, errorMessage }
  const candidateKeys = [
    'errorMessage',
    'ErrorMessage',
    'message',
    'error',
    'detail',
    'Message',
    'title',
    'description',
  ];
  for (let i = 0; i < candidateKeys.length; i += 1) {
    const key = candidateKeys[i];
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (Array.isArray(value) && value.length > 0) {
      const firstMessage = messageFromErrorItem(value[0]);
      if (firstMessage) return firstMessage;
    }
  }

  // Arrays of errors
  if (Array.isArray(data)) {
    const messages = data.map(messageFromErrorItem).filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  // { errors: ["..."] } or { errors: [{ errorMessage }] } — empty array falls through to errorMessage
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const messages = data.errors.map(messageFromErrorItem).filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  // ASP.NET Core validation: { errors: { "Name": ["required"], ... } }
  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    const keys = Object.keys(data.errors);
    for (let i = 0; i < keys.length; i += 1) {
      const value = data.errors[keys[i]];
      if (Array.isArray(value) && value.length > 0) {
        const first = value[0];
        if (typeof first === 'string' && first.trim()) return first;
      }
      if (typeof value === 'string' && value.trim()) return value;
    }
  }

  // ModelState-like objects: { Field: ["msg1", ...], OtherField: ["msg2"] }
  if (typeof data === 'object' && data !== null) {
    const objectKeys = Object.keys(data);
    for (let i = 0; i < objectKeys.length; i += 1) {
      const key = objectKeys[i];
      if (!METADATA_KEYS.has(key)) {
        const value = data[key];
        if (typeof value === 'string' && value.trim()) return value;
        if (Array.isArray(value) && value.length > 0) {
          const firstMessage = messageFromErrorItem(value[0]);
          if (firstMessage) return firstMessage;
        }
      }
    }
  }

  return undefined;
}

function extractCodeFromData(data) {
  if (!data || typeof data !== 'object') return undefined;
  const code = data.code ?? data.Code;
  return typeof code === 'string' && code.trim() ? code.trim() : undefined;
}

const AXIOS_GENERIC_STATUS_MESSAGE = /^Request failed with status code \d+$/i;

export function parseHttpError(error) {
  if (!error) {
    return { status: undefined, message: undefined, code: undefined, data: undefined, isAxiosError: false };
  }

  const response = error.response || null;
  const status = (response && response.status) || error.status;
  const responseData = (response && response.data) || error.data;
  let data = responseData;
  // Some callers reject the API body itself: { code, errorMessage, errors, traceId }.
  if (data == null && error && typeof error === 'object' && !error.isAxiosError && !response) {
    if (error.errorMessage || error.code) data = error;
  }
  const derivedMessage = extractMessageFromData(data);
  // Prefer API body text; ignore Axios' generic "Request failed with status code NNN".
  const rawErrorMessage =
    typeof error.message === 'string' && error.message.trim() && !AXIOS_GENERIC_STATUS_MESSAGE.test(error.message)
      ? error.message.trim()
      : undefined;
  const message = derivedMessage || rawErrorMessage;
  const code = extractCodeFromData(data);
  const isAxiosError = Boolean(response) || Boolean(error.isAxiosError);

  return { status, message, code, data, isAxiosError };
}

export function isBusinessUnauthorizedError(error) {
  const { status, code } = parseHttpError(error);
  return status === HttpStatus.UNAUTHORIZED && Boolean(code && BUSINESS_UNAUTHORIZED_CODES.has(code));
}

/** Axios reject shape used across the app (interceptor passes `response` when present). */
const GENERIC_AXIOS_MESSAGE = /^Request failed with status code \d+$/i;

export function normalizeHttpReject(error) {
  if (!error) return error;
  if (error instanceof Error && error.isHttpReject) return error;

  const response = error.response && error.response.status != null ? error.response : null;
  const source = response || error;
  const messageText = getApiErrorMessage(source, 'Sorry, an error occurred. Please try again later.');

  if (error instanceof Error) {
    if (!error.message || GENERIC_AXIOS_MESSAGE.test(error.message)) {
      error.message = messageText;
    }
    if (error.status == null && source.status != null) error.status = source.status;
    if (error.data === undefined && source.data !== undefined) error.data = source.data;
    error.isHttpReject = true;
    return error;
  }

  const wrapped = new Error(messageText || 'Sorry, an error occurred. Please try again later.');
  wrapped.status = source.status;
  wrapped.data = source.data;
  wrapped.headers = source.headers;
  wrapped.config = source.config || error.config;
  wrapped.response = response || undefined;
  wrapped.isAxiosError = true;
  wrapped.isHttpReject = true;
  return wrapped;
}

/**
 * Global 401 handling for the axios response interceptor.
 * @returns {'business'|'session'} How the caller should proceed.
 */
export function resolveUnauthorizedInterceptorAction(error) {
  if (isBusinessUnauthorizedError(error)) {
    return 'business';
  }
  return 'session';
}

/**
 * Marks a business 401 and returns the value to reject from the interceptor.
 */
export function prepareBusinessUnauthorizedReject(error) {
  const rejected = normalizeHttpReject(error);
  markBusinessUnauthorizedHandled(rejected);
  return rejected;
}

function getKnownApiErrorMessage(code) {
  if (!code) return undefined;
  return KNOWN_API_ERROR_MESSAGES[code];
}

/**
 * User-facing message for failed API calls (Axios response or normalized reject from interceptors).
 * 402 often has an empty body — always map to MSG_PAYMENT_REQUIRED unless the API sent a message.
 */
export function getApiErrorMessage(error, fallback) {
  const { status, message: parsed, code } = parseHttpError(error);
  if (status === HttpStatus.PAYMENT_REQUIRED) {
    if (parsed && String(parsed).trim()) return String(parsed).trim();
    return MSG_PAYMENT_REQUIRED;
  }
  if (parsed && String(parsed).trim()) return String(parsed).trim();
  const known = getKnownApiErrorMessage(code);
  if (known) return known;
  return fallback;
}

/** Message for 401 business-auth responses shown by the global interceptor. */
export function getBusinessUnauthorizedMessage(error) {
  return getApiErrorMessage(error, KNOWN_API_ERROR_MESSAGES.unauthorized_access);
}

/**
 * Resolves the message to show users, respecting global HTTP status handling (401/402).
 * Returns null when no user-facing message should be shown (e.g. 401 handled by interceptor).
 */
export function resolveApiErrorMessage(error, fallback, options = {}) {
  const { status, message, code } = parseHttpError(error);
  const {
    skipUnauthorized = true,
    paymentRequiredMessage = MSG_PAYMENT_REQUIRED,
    unauthorizedMessage = null,
  } = options;

  if (skipUnauthorized && status === HttpStatus.UNAUTHORIZED && !isBusinessUnauthorizedError(error)) {
    return unauthorizedMessage;
  }

  if (status === HttpStatus.PAYMENT_REQUIRED) {
    return paymentRequiredMessage;
  }

  if (message && String(message).trim()) {
    return String(message).trim();
  }

  const known = getKnownApiErrorMessage(code);
  if (known) return known;

  if (options.useCodeAsFallback && code) {
    return code.replace(/_/g, ' ');
  }

  return fallback;
}

/**
 * User-facing text from a successful API response body (e.g. { message: "..." }).
 */
export function getApiResponseMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data === 'string' && data.trim()) return data.trim();

  const candidateKeys = ['message', 'Message', 'errorMessage', 'ErrorMessage'];
  for (let i = 0; i < candidateKeys.length; i += 1) {
    const value = data[candidateKeys[i]];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  return fallback;
}

/**
 * Preferred BE error body: `{ code: string, errorMessage: string }`.
 * Also accepts bodies we can still parse a user message from (see extractMessageFromData).
 */
export function isStructuredApiErrorBody(data) {
  if (data == null) return false;
  if (typeof data === 'string') return Boolean(data.trim());
  if (typeof data !== 'object') return false;

  const code = extractCodeFromData(data);
  const message = extractMessageFromData(data);
  // Ideal contract
  if (code && message) return true;
  // Acceptable: message without code (FE can still toast)
  if (message) return true;
  return false;
}

function shouldReportUnstructuredApiErrors() {
  if (typeof process !== 'undefined' && process.env.REACT_APP_LOG_UNSTRUCTURED_API_ERRORS === 'true') {
    return true;
  }
  return typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
}

const UNSTRUCTURED_API_ERROR_KEY = 'cc.unstructuredApiErrors';
const WINDOW_REPORT_KEY = 'ccUnstructuredApiErrors';
const WINDOW_CLEAR_KEY = 'ccClearUnstructuredApiErrors';

function syncUnstructuredApiErrorWindow(entries) {
  if (typeof window === 'undefined') return;
  window[WINDOW_REPORT_KEY] = entries;
}

function readUnstructuredApiErrorStore() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(UNSTRUCTURED_API_ERROR_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

function writeUnstructuredApiErrorStore(entries) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(UNSTRUCTURED_API_ERROR_KEY, JSON.stringify(entries.slice(-100)));
  } catch (err) {
    // ignore quota / private mode
  }
}

/**
 * Snapshot of API failures that lacked a structured `{ code, errorMessage }` (or parseable message).
 * Use in DevTools: `copy(window.ccUnstructuredApiErrors)` after exercising flows.
 */
export function getUnstructuredApiErrorReport() {
  return readUnstructuredApiErrorStore();
}

export function clearUnstructuredApiErrorReport() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(UNSTRUCTURED_API_ERROR_KEY);
  } catch (err) {
    // ignore
  }
  syncUnstructuredApiErrorWindow([]);
}

/**
 * Records HTTP errors with empty/unstructured bodies so we can ask BE to fix them.
 * No-op in production unless REACT_APP_LOG_UNSTRUCTURED_API_ERRORS=true.
 */
export function reportUnstructuredApiError(error) {
  if (!shouldReportUnstructuredApiErrors()) return;

  const response = error?.response || (error?.status ? error : null);
  if (!response?.status) return;

  const { status, data, config: responseConfig } = response;
  // Only care about client/server error responses
  if (status < 400) return;

  if (isStructuredApiErrorBody(data)) return;

  const config = error?.config || responseConfig || {};
  const method = String(config.method || 'GET').toUpperCase();
  const url = config.url || response.request?.responseURL || '(unknown url)';
  const bodyPreview =
    data == null || data === ''
      ? '(empty)'
      : typeof data === 'string'
        ? data.slice(0, 200)
        : JSON.stringify(data).slice(0, 200);

  const entry = {
    method,
    url,
    status,
    bodyPreview,
    at: new Date().toISOString(),
  };
  const key = `${method} ${url} ${status}`;
  const existing = readUnstructuredApiErrorStore();
  if (existing.some((item) => `${item.method} ${item.url} ${item.status}` === key)) {
    return;
  }

  const next = [...existing, entry];
  writeUnstructuredApiErrorStore(next);
  syncUnstructuredApiErrorWindow(next);

  // eslint-disable-next-line no-console
  console.warn('[API] Unstructured error body (ask BE to return { code, errorMessage })', entry);
}

/** Expose report helpers on window in non-production for easy copy/paste to BE. */
export function attachUnstructuredApiErrorDebugHelpers() {
  if (typeof window === 'undefined' || !shouldReportUnstructuredApiErrors()) return;
  syncUnstructuredApiErrorWindow(getUnstructuredApiErrorReport());
  window[WINDOW_CLEAR_KEY] = clearUnstructuredApiErrorReport;
}
